import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BiCalendar, BiSearchAlt, BiUser } from "react-icons/bi";
import { FaPlane, FaRegCalendarAlt } from "react-icons/fa";
import { IoChevronDown, IoLocationSharp } from "react-icons/io5";
import { MdFlight, MdSwapVert } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../Deal/Deal.css";
import { airports } from "./FlightList";

const datePickerStyles = `
  .custom-datepicker .react-datepicker {
    border: 1px solid #1e88e5;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(30, 136, 229, 0.2);
  }
  
  .custom-datepicker .react-datepicker__header {
    background: linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%);
    border-bottom: 1px solid #1e88e5;
  }
  
  .custom-datepicker .react-datepicker__current-month {
    color: white;
    font-weight: 600;
  }
  
  .custom-datepicker .react-datepicker__day-name {
    color: rgba(255, 255, 255, 0.9);
  }
  
  .custom-datepicker .react-datepicker__day--selected {
    background: linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%);
    border-radius: 50%;
  }
  
  .disabled-date-input {
    cursor: not-allowed !important;
    opacity: 0.6 !important;
    background-color: #f8fafc !important;
    pointer-events: none !important;
  }
  
  .loading-suggestions {
    padding: 10px;
    color: #666;
    font-style: italic;
    text-align: center;
    font-size: 0.9rem;
  }
  
  .api-warning {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    color: #856404;
    padding: 8px 12px;
    border-radius: 4px;
    margin-top: 10px;
    font-size: 0.9rem;
    text-align: center;
  }
  
  .suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1000;
    margin-top: 4px;
  }
  
  .suggestion-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
    transition: background-color 0.2s;
  }
  
  .suggestion-item:hover {
    background-color: #f8fafc;
  }
  
  .suggestion-item:last-child {
    border-bottom: none;
  }
  
  .suggestion-icon {
    margin-right: 10px;
    color: #1e88e5;
    font-size: 1.2rem;
  }
  
  .airport-code {
    font-weight: 600;
    color: #0d47a1;
    font-size: 0.9rem;
  }
  
  .airport-name {
    color: #64748b;
    font-size: 0.85rem;
    margin-top: 2px;
  }
  
  .input-with-suggestions {
    position: relative;
  }
  
  /* iOS Date Picker Fix - Add only this line */
  .date-input {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
`;

// Custom hook for debouncing
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Helper function to create URL parameters
const createSearchParams = (searchData) => {
  const params = new URLSearchParams();
  params.set("origin", searchData.origin);
  params.set("destination", searchData.destination);
  params.set("departureDate", searchData.departureDate);
  if (searchData.returnDate) params.set("returnDate", searchData.returnDate);
  params.set("travelers", searchData.travelers.toString());
  params.set("tripType", searchData.tripType);
  params.set("travelClass", searchData.travelClass || "Economy");
  params.set("originName", searchData.originName);
  params.set("destinationName", searchData.destinationName);

  // Add passenger details to URL
  params.set("adults", searchData.passengerDetails.adults.toString());
  params.set("children", searchData.passengerDetails.children.toString());
  params.set("infants", searchData.passengerDetails.infants.toString());

  return params;
};

export default function Deal({ showHeader = true }) {
  const navigate = useNavigate();
  const [isOneWay, setIsOneWay] = useState(false);
  const [depart, setDepart] = useState("London All Airport");
  const [arrive, setArrive] = useState("");
  const [departSuggestions, setDepartSuggestions] = useState([]);
  const [arriveSuggestions, setArriveSuggestions] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [leaveDate, setLeaveDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [departCode, setDepartCode] = useState("LON");
  const [arriveCode, setArriveCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingAirports, setIsFetchingAirports] = useState(false);
  const [isSearchingDepart, setIsSearchingDepart] = useState(false);
  const [isSearchingArrive, setIsSearchingArrive] = useState(false);
  const leaveDatePickerRef = useRef(null);
  const returnDatePickerRef = useRef(null);
  const [errors, setErrors] = useState({
    departFrom: "",
    arriveAt: "",
    leaveOn: "",
    returnOn: "",
  });
  const [activeField, setActiveField] = useState(null);
  const [passengerCount, setPassengerCount] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });
  const [isPassengerDropdownOpen, setIsPassengerDropdownOpen] = useState(false);

  // New state for API data
  const [airportData, setAirportData] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [initialAirportsLoaded, setInitialAirportsLoaded] = useState(false);

  // Debounced search terms
  const debouncedDepart = useDebounce(depart, 500);
  const debouncedArrive = useDebounce(arrive, 500);

  const addOneDay = (date) => {
    if (!date) return null;
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  };

  // Initialize airport data from API
  useEffect(() => {
    const fetchAirports = async () => {
      setIsFetchingAirports(true);
      setApiError(null);

      try {
        const apiUrl = `${process.env.REACT_APP_BASE_URL}/airports/search`;
        const response = await axios.get(apiUrl, {
          timeout: 10000,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.data?.success && Array.isArray(response.data.data)) {
          const processedAirports = response.data.data.map(
            (airport, index) => ({
              id: airport._id || `airport-${index}`,
              airportName: airport.name || "",
              airportCode: airport.code || "",
              fullName: `${airport.name} (${airport.code})`,
            }),
          );

          setAirportData(processedAirports);
          setInitialAirportsLoaded(true);
        } else {
          setAirportData(getProcessedLocalAirports());
          setInitialAirportsLoaded(true);
        }
      } catch (error) {
        setAirportData(getProcessedLocalAirports());
        setInitialAirportsLoaded(true);
      } finally {
        setIsFetchingAirports(false);
      }
    };

    fetchAirports();
  }, []);

  // Close passenger dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isPassengerDropdownOpen &&
        !event.target.closest(".passenger-dropdown-container")
      ) {
        setIsPassengerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPassengerDropdownOpen]);

  // Fallback function to process local airports
  const getProcessedLocalAirports = useCallback(() => {
    if (!airports || !Array.isArray(airports)) {
      return [];
    }

    return airports.map((airportStr, index) => {
      if (typeof airportStr !== "string") {
        return {
          id: `local-${index}`,
          airportName: "Unknown Airport",
          airportCode: "",
          fullName: "Unknown Airport",
        };
      }

      const matches = airportStr.match(/(.*?)\s*\((.*?)\)/);
      return {
        id: `local-${index}`,
        airportName: matches ? matches[1].trim() : airportStr,
        airportCode: matches ? matches[2].trim() : "",
        fullName: airportStr,
      };
    });
  }, []);

  // Search airports locally (fallback function)
  const searchLocalAirports = useCallback(
    (searchTerm) => {
      if (!searchTerm.trim() || searchTerm.length < 1) {
        return [];
      }

      const search = searchTerm.toLowerCase();
      return airportData
        .filter((airport) => {
          const codeMatch = airport.airportCode.toLowerCase();
          const nameMatch = airport.airportName.toLowerCase();
          return codeMatch.includes(search) || nameMatch.includes(search);
        })
        .slice(0, 10);
    },
    [airportData],
  );

  // Fetch airport suggestions from API
  const fetchAirportSuggestions = useCallback(
    async (searchTerm, type) => {
      if (!searchTerm.trim() || searchTerm.length < 1) {
        return [];
      }

      // If we don't have airport data yet, use local search
      if (!initialAirportsLoaded && airportData.length === 0) {
        return searchLocalAirports(searchTerm);
      }

      // Try API first if we have a base URL
      if (process.env.REACT_APP_BASE_URL) {
        try {
          const apiUrl = `${process.env.REACT_APP_BASE_URL}/airports/search`;
          const response = await axios.get(apiUrl, {
            params: {
              search: searchTerm,
              limit: 10,
            },
            timeout: 5000,
          });

          if (response.data.success && Array.isArray(response.data.data)) {
            return response.data.data.map((airport, index) => ({
              id: airport._id || `api-${index}`,
              airportName: airport.name || "",
              airportCode: airport.code || "",
              fullName: `${airport.name} (${airport.code})`,
            }));
          }
        } catch (error) {
          console.log(
            "API search failed, falling back to local search:",
            error.message,
          );
          // Fall through to local search
        }
      }

      // Fallback to local search
      return searchLocalAirports(searchTerm);
    },
    [initialAirportsLoaded, airportData, searchLocalAirports],
  );

  // Effect for departure search suggestions
  useEffect(() => {
    const searchDepartureAirports = async () => {
      if (debouncedDepart && activeField === "depart") {
        setIsSearchingDepart(true);
        const suggestions = await fetchAirportSuggestions(
          debouncedDepart,
          "departure",
        );
        setDepartSuggestions(suggestions);
        setIsSearchingDepart(false);
      } else if (!debouncedDepart) {
        setDepartSuggestions([]);
      }
    };

    searchDepartureAirports();
  }, [debouncedDepart, activeField, fetchAirportSuggestions]);

  // Effect for arrival search suggestions
  useEffect(() => {
    const searchArrivalAirports = async () => {
      if (debouncedArrive && activeField === "arrive") {
        setIsSearchingArrive(true);
        const suggestions = await fetchAirportSuggestions(
          debouncedArrive,
          "arrival",
        );
        setArriveSuggestions(suggestions);
        setIsSearchingArrive(false);
      } else if (!debouncedArrive) {
        setArriveSuggestions([]);
      }
    };

    searchArrivalAirports();
  }, [debouncedArrive, activeField, fetchAirportSuggestions]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  useEffect(() => {
    if (!document.getElementById("date-picker-disabled-styles")) {
      const styleElement = document.createElement("style");
      styleElement.id = "date-picker-disabled-styles";
      styleElement.innerHTML = datePickerStyles;
      document.head.appendChild(styleElement);

      return () => {
        const existingStyle = document.getElementById(
          "date-picker-disabled-styles",
        );
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, []);

  // Validation functions
  const validateDepart = (depart) => (!depart ? "*Departure is required" : "");
  const validateArrive = (arrive) => (!arrive ? "*Arrival is required" : "");
  const validateLeaveDate = (leaveDate) =>
    !leaveDate ? "*Leave date is required" : "";
  const validateReturnDate = (returnDate) =>
    !isOneWay && !returnDate ? "*Return date is required" : "";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let error = "";

    const validators = {
      departFrom: validateDepart,
      arriveAt: validateArrive,
      leaveOn: validateLeaveDate,
      returnOn: validateReturnDate,
    };

    error = validators[name] ? validators[name](value) : "";
    setErrors({ ...errors, [name]: error });
  };

  const handleDepartChange = (e) => {
    const value = e.target.value;
    setDepart(value);
    handleInputChange({ target: { name: "departFrom", value } });

    // Clear suggestions if input is cleared
    if (value.trim() === "") {
      setDepartSuggestions([]);
    }
  };

  const handleArriveChange = (e) => {
    const value = e.target.value;
    setArrive(value);
    handleInputChange({ target: { name: "arriveAt", value } });

    // Clear suggestions if input is cleared
    if (value.trim() === "") {
      setArriveSuggestions([]);
    }
  };

  const handleAirportSelect = (airport, type) => {
    if (type === "departure") {
      setDepart(airport.airportName);
      setDepartCode(airport.airportCode);
      setDepartSuggestions([]);
      handleInputChange({
        target: { name: "departFrom", value: airport.airportName },
      });
    } else {
      setArrive(airport.airportName);
      setArriveCode(airport.airportCode);
      setArriveSuggestions([]);
      handleInputChange({
        target: { name: "arriveAt", value: airport.airportName },
      });
    }
  };

  const handleSearchOnly = () => {
    // Validate required fields for search
    const searchErrors = {
      departFrom: validateDepart(depart),
      arriveAt: validateArrive(arrive),
      leaveOn: validateLeaveDate(leaveDate),
      returnOn: !isOneWay ? validateReturnDate(returnDate) : "",
    };

    setErrors(searchErrors);

    const hasSearchErrors = Object.values(searchErrors).some(
      (error) => error !== "",
    );
    if (hasSearchErrors) return;

    setIsLoading(true);

    const formatDateForStorage = (date) => {
      const d = new Date(date);
      d.setHours(12, 0, 0, 0);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;
    };

    // FIXED: Use TOTAL count (adults + children + infants) for travelers
    const totalTravelers =
      passengerCount.adults + passengerCount.children + passengerCount.infants;
    const searchData = {
      origin: departCode || "LON",
      originName: depart || "London All Airport",
      destination: arriveCode || "",
      destinationName: arrive || "",
      departureDate: leaveDate ? formatDateForStorage(leaveDate) : "",
      returnDate: returnDate ? formatDateForStorage(returnDate) : "",
      travelers: totalTravelers || 1,
      tripType: isOneWay ? "one-way" : "round-trip",
      travelClass: "Economy",
      passengerDetails: passengerCount, // Include full passenger details
    };

    console.log("Search data with passenger details:", searchData);

    // Store in sessionStorage for backward compatibility
    sessionStorage.setItem("flightSearchParams", JSON.stringify(searchData));

    // Create URL parameters like Book.js does (with passenger details)
    const params = createSearchParams(searchData);

    // Navigate to flight result with URL parameters
    navigate(`/flight-result?${params.toString()}`);

    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let data = {};

    data.departFrom = depart;
    data.arriveAt = arrive;
    data.tripType = isOneWay ? "One Way" : "Return";
    data.leaveOn = leaveDate ? leaveDate.toISOString().split("T")[0] : "";
    data.returnOn = returnDate ? returnDate.toISOString().split("T")[0] : "";
    data.class = "Economy";
    data.tagLine = "Homepage enquiry form";

    const newErrors = {
      departFrom: validateDepart(data.departFrom),
      arriveAt: validateArrive(data.arriveAt),
      leaveOn: validateLeaveDate(data.leaveOn),
      returnOn: validateReturnDate(data.returnOn),
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) return;

    setIsDisabled(true);

    const formatDateForStorage = (date) => {
      const d = new Date(date);
      d.setHours(12, 0, 0, 0);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;
    };

    // FIXED: Use TOTAL count (adults + children + infants) for travelers
    const totalTravelers =
      passengerCount.adults + passengerCount.children + passengerCount.infants;
    const searchData = {
      origin: departCode || "LON",
      originName: depart || "London All Airport",
      destination: arriveCode || "",
      destinationName: arrive || "",
      departureDate: leaveDate ? formatDateForStorage(leaveDate) : "",
      returnDate: returnDate ? formatDateForStorage(returnDate) : "",
      travelers: totalTravelers || 1,
      tripType: isOneWay ? "one-way" : "round-trip",
      travelClass: "Economy",
      passengerDetails: passengerCount,
    };

    console.log("Search data with passenger details:", searchData);
    sessionStorage.setItem("flightSearchParams", JSON.stringify(searchData));

    // Create URL parameters (with passenger details)
    const params = createSearchParams(searchData);

    Swal.fire({
      title: "Flight Search Submitted!",
      text: "Searching for flights...",
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#1e88e5",
      background: "#f8fafc",
      color: "#0d47a1",
    }).then(() => {
      // Navigate to flight results with URL parameters
      navigate(`/flight-result?${params.toString()}`);
    });

    setIsDisabled(false);
  };

  const CustomDatePickerInput = ({
    value,
    onClick,
    placeholder,
    isDisabled,
  }) => (
    <div className="relative">
      <input
        type="text"
        value={value}
        onClick={isDisabled ? null : onClick}
        placeholder={placeholder}
        readOnly
        className={`date-input ${isDisabled ? "disabled-date-input" : ""}`}
        style={
          isDisabled
            ? {
                cursor: "not-allowed",
                opacity: isMobile ? 0.4 : 0.6,
                backgroundColor: "#f8fafc",
                pointerEvents: "none",
              }
            : {}
        }
        // ONLY iOS FIX ADDED HERE:
        onFocus={(e) => e.target.blur()}
      />
      <div
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-400 cursor-pointer"
        onClick={isDisabled ? null : onClick}
      >
        <FaRegCalendarAlt />
      </div>
    </div>
  );

  const handleLeaveDateChange = (date) => {
    setLeaveDate(date);
    handleInputChange({ target: { name: "leaveOn", value: date } });

    if (returnDate && returnDate < date) {
      setReturnDate(null);
    }
  };

  const handleReturnDateChange = (date) => {
    setReturnDate(date);
    handleInputChange({ target: { name: "returnOn", value: date } });
  };

  const swapLocations = () => {
    const tempDepart = depart;
    const tempDepartCode = departCode;
    setDepart(arrive);
    setDepartCode(arriveCode);
    setArrive(tempDepart);
    setArriveCode(tempDepartCode);
  };

  return (
    <div id="#h-fh" className="flight-booking-container">
      <div className="booking-wrapper">
        {apiError && <div className="api-warning">⚠️ {apiError}</div>}

        <form className="booking-form" onSubmit={handleSubmit}>
          <div className="trip-type-selector">
            <div className="trip-type-buttons">
              <button
                type="button"
                className={`trip-btn ${!isOneWay ? "active" : ""}`}
                onClick={() => setIsOneWay(false)}
              >
                <MdSwapVert className="btn-icon" />
                Return Trip
              </button>
              <button
                type="button"
                className={`trip-btn ${isOneWay ? "active" : ""}`}
                onClick={() => setIsOneWay(true)}
              >
                <FaPlane className="btn-icon" />
                One Way
              </button>
            </div>
          </div>

          <div className="form-grid">
            {/* Departure Field */}
            <div className="form-group">
              <label className="form-label">
                <IoLocationSharp className="label-icon" />
                Depart From *
              </label>
              <div className="input-with-suggestions">
                <input
                  type="text"
                  autoComplete="off"
                  className={`form-input ${
                    activeField === "depart" ? "active" : ""
                  }`}
                  value={depart}
                  onChange={handleDepartChange}
                  onFocus={() => {
                    setActiveField("depart");
                    if (depart === "London All Airport") {
                      setDepart("");
                    }
                  }}
                  onBlur={() => setTimeout(() => setActiveField(null), 200)}
                  name="departFrom"
                  disabled={isLoading}
                  placeholder="City or Airport"
                />
                {errors.departFrom && (
                  <span className="error-message">{errors.departFrom}</span>
                )}

                {departSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {departSuggestions.map((airport) => (
                      <div
                        key={airport.id}
                        onClick={() =>
                          handleAirportSelect(airport, "departure")
                        }
                        className="suggestion-item"
                      >
                        <MdFlight className="suggestion-icon" />
                        <div>
                          <div className="airport-code">
                            {airport.airportCode}
                          </div>
                          <div className="airport-name">
                            {airport.airportName}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Arrival Field */}
            <div className="form-group">
              <label className="form-label">
                <IoLocationSharp className="label-icon" />
                Arrive At *
              </label>
              <div className="input-with-suggestions">
                <input
                  type="text"
                  autoComplete="off"
                  className={`form-input ${
                    activeField === "arrive" ? "active" : ""
                  }`}
                  value={arrive}
                  onChange={handleArriveChange}
                  onFocus={() => setActiveField("arrive")}
                  onBlur={() => setTimeout(() => setActiveField(null), 200)}
                  name="arriveAt"
                  disabled={isLoading}
                  placeholder="City or Airport"
                />
                {errors.arriveAt && (
                  <span className="error-message">{errors.arriveAt}</span>
                )}

                {arriveSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {arriveSuggestions.map((airport) => (
                      <div
                        key={airport.id}
                        onClick={() => handleAirportSelect(airport, "arrival")}
                        className="suggestion-item"
                      >
                        <MdFlight className="suggestion-icon" />
                        <div>
                          <div className="airport-code">
                            {airport.airportCode}
                          </div>
                          <div className="airport-name">
                            {airport.airportName}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Leave Date */}
            <div className="form-group">
              <label className="form-label">
                <BiCalendar className="label-icon" />
                Leave On *
              </label>
              <div className="date-picker-wrapper custom-datepicker">
                <DatePicker
                  selected={leaveDate}
                  onChange={handleLeaveDateChange}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select Date"
                  name="leaveOn"
                  shouldCloseOnSelect={true}
                  withPortal={isMobile}
                  portalId="leave-date-portal"
                  ref={leaveDatePickerRef}
                  customInput={
                    <CustomDatePickerInput
                      placeholder="DD/MM/YYYY"
                      isDisabled={false}
                    />
                  }
                  popperClassName="date-picker-popper"
                  // ONLY iOS FIX ADDED HERE:
                  onFocus={(e) => e.target.blur()}
                />
              </div>
              {errors.leaveOn && (
                <span className="error-message">{errors.leaveOn}</span>
              )}
            </div>

            {/* Return Date */}
            {!isOneWay && (
              <div className="form-group">
                <label className="form-label">
                  <BiCalendar className="label-icon" />
                  Return On *
                </label>
                <div className="date-picker-wrapper custom-datepicker">
                  <DatePicker
                    selected={returnDate}
                    onChange={handleReturnDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    minDate={leaveDate ? addOneDay(leaveDate) : new Date()}
                    disabled={!leaveDate}
                    name="returnOn"
                    shouldCloseOnSelect={true}
                    withPortal={isMobile}
                    portalId="return-date-portal"
                    ref={returnDatePickerRef}
                    customInput={
                      <CustomDatePickerInput
                        placeholder="DD/MM/YYYY"
                        isDisabled={!leaveDate}
                      />
                    }
                    popperClassName="date-picker-popper"
                    // ONLY iOS FIX ADDED HERE:
                    onFocus={(e) => e.target.blur()}
                  />
                </div>
                {errors.returnOn && (
                  <span className="error-message">{errors.returnOn}</span>
                )}
              </div>
            )}

            {/* Passengers Dropdown */}
            <div className="form-group">
              <label className="form-label">
                <BiUser className="label-icon" />
                Passengers
              </label>
              <div className="passenger-dropdown-container">
                <div
                  className="passenger-input"
                  onClick={() =>
                    setIsPassengerDropdownOpen(!isPassengerDropdownOpen)
                  }
                >
                  <span className="passenger-summary">
                    {passengerCount.adults +
                      passengerCount.children +
                      passengerCount.infants}{" "}
                    Passenger
                    {passengerCount.adults +
                      passengerCount.children +
                      passengerCount.infants !==
                    1
                      ? "s"
                      : ""}
                  </span>
                  <IoChevronDown
                    className={`dropdown-arrow ${isPassengerDropdownOpen ? "rotated" : ""}`}
                  />
                </div>

                {isPassengerDropdownOpen && (
                  <div className="passenger-dropdown">
                    <div className="passenger-option">
                      <div className="passenger-type">
                        <span className="type-label">Adults</span>
                        <span className="type-description">12+ Years</span>
                      </div>
                      <div className="passenger-counter">
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (passengerCount.adults > 1) {
                              setPassengerCount((prev) => ({
                                ...prev,
                                adults: prev.adults - 1,
                              }));
                            }
                          }}
                          disabled={passengerCount.adults <= 1}
                        >
                          −
                        </button>
                        <span className="counter-value">
                          {passengerCount.adults}
                        </span>
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              passengerCount.adults + passengerCount.children <
                              9
                            ) {
                              setPassengerCount((prev) => ({
                                ...prev,
                                adults: prev.adults + 1,
                              }));
                            }
                          }}
                          disabled={
                            passengerCount.adults + passengerCount.children >= 9
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="passenger-option">
                      <div className="passenger-type">
                        <span className="type-label">Child</span>
                        <span className="type-description">2-11 Years</span>
                      </div>
                      <div className="passenger-counter">
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (passengerCount.children > 0) {
                              setPassengerCount((prev) => ({
                                ...prev,
                                children: prev.children - 1,
                              }));
                            }
                          }}
                          disabled={passengerCount.children <= 0}
                        >
                          −
                        </button>
                        <span className="counter-value">
                          {passengerCount.children}
                        </span>
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              passengerCount.adults + passengerCount.children <
                              9
                            ) {
                              setPassengerCount((prev) => ({
                                ...prev,
                                children: prev.children + 1,
                              }));
                            }
                          }}
                          disabled={
                            passengerCount.adults + passengerCount.children >= 9
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="passenger-option">
                      <div className="passenger-type">
                        <span className="type-label">Infants</span>
                        <span className="type-description">0-2 Years</span>
                      </div>
                      <div className="passenger-counter">
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (passengerCount.infants > 0) {
                              setPassengerCount((prev) => ({
                                ...prev,
                                infants: prev.infants - 1,
                              }));
                            }
                          }}
                          disabled={passengerCount.infants <= 0}
                        >
                          −
                        </button>
                        <span className="counter-value">
                          {passengerCount.infants}
                        </span>
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              passengerCount.infants < passengerCount.adults &&
                              passengerCount.infants < 9
                            ) {
                              setPassengerCount((prev) => ({
                                ...prev,
                                infants: prev.infants + 1,
                              }));
                            }
                          }}
                          disabled={
                            passengerCount.infants >= passengerCount.adults ||
                            passengerCount.infants >= 9
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="done-btn"
                      onClick={() => setIsPassengerDropdownOpen(false)}
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              type="button"
              className="submit-btn secondary"
              onClick={handleSearchOnly}
              disabled={isLoading}
            >
              <BiSearchAlt className="btn-icon" />
              {isLoading ? "Searching..." : "SEARCH FLIGHTS"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
